import { SyntheticEvent, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux';
import { PageTabValue, PageTabsKeyEnum, selectPageTab, setPageTab } from '~/redux/slices/pageTabsSlice';

export const usePersistedTabValue = <T extends PageTabValue>(page: PageTabsKeyEnum, defaultTab?: T) => {
  const dispatch = useAppDispatch();
  const savedTab = (useAppSelector(selectPageTab(page)) as T | undefined) ?? defaultTab;

  const setTab = useCallback((tab: T) => dispatch(setPageTab({ page, tab })), [page, dispatch]);

  return [savedTab, setTab] as const;
};

const usePersistedTab = (page: PageTabsKeyEnum, visibleTabs: { label: string; value?: string }[]) => {
  const [savedTab, setTab] = usePersistedTabValue<string>(page);

  // Match on a stable value when provided, since labels can change at runtime (e.g. row counts)
  const getTabId = (tab: { label: string; value?: string }) => tab.value ?? tab.label;

  const selectedTabIndex = Math.max(
    0,
    visibleTabs.findIndex((tab) => getTabId(tab) === savedTab),
  );

  const handleTabChange = (_: SyntheticEvent, newIndex: number) => {
    const nextTab = visibleTabs[newIndex];

    if (nextTab !== undefined) setTab(getTabId(nextTab));
  };

  return { selectedTabIndex, handleTabChange };
};

export default usePersistedTab;
